import { useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { Controller, type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { PaletteIcon } from '@blade/ui-kit'
import { WalletColorSwatch } from '@root/features/wallet/wallet-color-swatch'
import { ChevronDownExactIcon } from '@root/shared/ui-kit'

import { EditWalletPanelItem } from './edit-wallet-panel-item'

import type { EditWalletValues } from './edit-wallet'
import type { WalletModel } from '@root/entities/wallet'

type Props = {
  wallet: WalletModel
  walletColor: string
  control: Control<EditWalletValues>
}

export const EditWalletColor: React.FC<Props> = ({
  wallet,
  walletColor,
  control,
}) => {
  const { t } = useTranslation()
  const [currentColor] = useAtom(wallet.color)

  const [expanded, setExpanded] = useState(false)

  const toggleExpand = () => setExpanded((it) => !it)

  return (
    <Root>
      <EditWalletPanelItem
        icon={<PaletteIcon size={16} />}
        endContent={
          <AppliedColor aria-expanded={expanded}>
            <ColorDot style={{ backgroundColor: walletColor }} />
            <ChevronDownExactIcon size={10} />
          </AppliedColor>
        }
        title={t('wallet-manager.add_color')}
        onClick={toggleExpand}
      />
      <AnimatePresence>
        {expanded ? (
          <ColorGrid
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <Controller
              control={control}
              name="color"
              rules={{ minLength: 2, required: true }}
              render={({ field }) => (
                <WalletColorSwatch
                  value={field.value}
                  onChange={field.onChange}
                  currentColor={currentColor}
                  size="s"
                />
              )}
            />
          </ColorGrid>
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Root = styled.div`
  background: var(--background-2);
  border-radius: 8px;
`

const ColorGrid = styled(motion.div)`
  display: grid;
  grid-auto-flow: column;

  padding: 10px 8px;
  gap: 2px;
`

const ColorDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  place-self: center;
`

const AppliedColor = styled.div`
  background: var(--backgroundA-3);
  border-radius: 6px;
  padding: 5px;

  display: grid;
  grid-template-columns: auto auto;
  gap: 5px;

  svg {
    transition: all 0.2s ease-in-out;
  }

  &[aria-expanded='true'] {
    svg {
      transform: rotate(180deg);
    }
  }
`
